const XLINK_NS = 'http://www.w3.org/1999/xlink';

/**
 * Figma SVGs put raster hrefs on xlink:href. html-to-image reads SVGImageElement.href
 * and, when that is empty, replaces it — which drops pattern backgrounds and card art.
 */
export function promoteSvgImageHrefs(root: ParentNode): void {
  root.querySelectorAll('image').forEach((node) => {
    const xlink = node.getAttributeNS(XLINK_NS, 'href') || node.getAttribute('xlink:href') || '';
    const href = node.getAttribute('href') || '';
    if (!xlink || href.startsWith('data:')) {
      return;
    }
    if (!href) {
      node.setAttribute('href', xlink);
    }
  });
}

/** html-to-image uses querySelector('#id'); IDs must be valid CSS identifiers. */
function isValidCssIdSelector(id: string): boolean {
  return /^[a-zA-Z_][\w-]*$/.test(id);
}

/** Replace each #id token once so a shorter id cannot rewrite a longer one that shares its stem. */
function replaceIdReferences(value: string, idMap: Map<string, string>): string {
  return value.replace(/#([^\s"')]+)/g, (match, id: string) => {
    const newId = idMap.get(id);
    return newId ? `#${newId}` : match;
  });
}

const REFERENCE_ATTRIBUTES = [
  'href',
  'xlink:href',
  'clip-path',
  'mask',
  'filter',
  'fill',
  'stroke',
  'marker-start',
  'marker-mid',
  'marker-end',
];

/**
 * Ids stay on the live off-screen tree between captures. An index from
 * querySelectorAll would reuse `cap5` after an edit shifts nodes, so two
 * elements can share an id and SVG <use> / clip-path resolve to the wrong one.
 */
let nextCaptureId = 0;

function allocateCaptureId(taken: Set<string>): string {
  let candidate = `cap${nextCaptureId++}`;
  while (taken.has(candidate)) {
    candidate = `cap${nextCaptureId++}`;
  }
  taken.add(candidate);
  return candidate;
}

/**
 * Rewrites invalid element ids under `root` so html-to-image can resolve SVG <use> targets.
 * Mutates the live subtree — call only on the off-screen capture root.
 */
export function fixCaptureDomIds(root: HTMLElement): void {
  const idMap = new Map<string, string>();
  const elementsWithId = root.querySelectorAll('[id]');
  const taken = new Set<string>();
  elementsWithId.forEach((element) => {
    if (element.id) {
      taken.add(element.id);
    }
  });

  elementsWithId.forEach((element) => {
    const oldId = element.id;
    if (!oldId || isValidCssIdSelector(oldId)) {
      return;
    }
    const newId = allocateCaptureId(taken);
    idMap.set(oldId, newId);
    element.id = newId;
    taken.delete(oldId);
  });

  if (idMap.size === 0) {
    return;
  }

  root.querySelectorAll('*').forEach((element) => {
    for (const attribute of REFERENCE_ATTRIBUTES) {
      const value = element.getAttribute(attribute);
      if (!value || value.startsWith('data:')) {
        continue;
      }
      const replaced = replaceIdReferences(value, idMap);
      if (replaced !== value) {
        element.setAttribute(attribute, replaced);
      }
    }

    if (element instanceof HTMLElement && element.style) {
      const style = element.getAttribute('style');
      if (style) {
        const replaced = replaceIdReferences(style, idMap);
        if (replaced !== style) {
          element.setAttribute('style', replaced);
        }
      }
    }
  });
}
